import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from './useFetch'
import { useAuth } from '../contexts/AuthContext'

import { CONFIG } from '../config'
import { ApiError } from '../types/exception/errorStatus'
import { getErrorMessage } from '../utils/exception/exceptionUtils'

export const apiUrl = (path: string) => `${CONFIG.BOUNCE_API}/api/v1${path}`

export const toQuery = (
  params: Record<string, string | number | boolean | undefined | null>
) => {
  const query = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, String(value))
    }
  })
  const text = query.toString()
  return text ? `?${text}` : ''
}

/**
 * Wraps authenticated API calls with a loading flag and a translated error message. `run` rethrows (as an Error with
 * the translated message) so callers can stop a flow; `error` keeps the last message for display.
 */
export function useApiRequest() {
  const { t } = useTranslation('exceptions')
  const { authInfo } = useAuth()
  const api = useFetch(authInfo?.accessToken, authInfo?.refreshToken)
  // useFetch returns a new object whenever its own flags change; keep `run` stable so effects depending on it do
  // not re-fire, while always calling the latest client (current token).
  const apiRef = useRef(api)
  apiRef.current = api
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const toMessage = useCallback(
    (caught: unknown) =>
      caught instanceof ApiError
        ? getErrorMessage(caught.data)
        : caught instanceof Error && caught.message
          ? caught.message
          : t('exceptions:unknown'),
    [t]
  )

  const run = useCallback(
    async <T>(call: (http: typeof api) => Promise<T>): Promise<T> => {
      setLoading(true)
      setError(null)
      try {
        return await call(apiRef.current)
      } catch (caught) {
        const message = toMessage(caught)
        setError(message)
        const wrapped = new Error(message) as Error & { apiError?: ApiError }
        if (caught instanceof ApiError) {
          wrapped.apiError = caught
        }
        throw wrapped
      } finally {
        setLoading(false)
      }
    },
    [toMessage]
  )

  const resetState = useCallback(() => {
    setLoading(false)
    setError(null)
  }, [])

  return useMemo(
    () => ({ run, loading, error, resetState }),
    [run, loading, error, resetState]
  )
}

/** Backend message key of an error thrown by `useApiRequest().run`, if it came from the API. */
export const apiMessageKey = (caught: unknown): string | undefined =>
  (caught as { apiError?: ApiError })?.apiError?.data.messageKey
